import { rpcClient, type RPCMutationResult, type RPCQueryResult } from "eddev/hooks"
import { onSnapshot, types } from "mobx-state-tree"
import { safeStorage } from "@utils/safe-storage"
import { getEncryptedSessionKey } from "./tnew-browser-session"

const STORAGE_KEY = "carriageworks:tess-auth"

type AuthSummary = RPCQueryResult<"api.public.tess.auth.session">
type SignInSummary = RPCMutationResult<"api.public.tess.auth.signIn">
type SignOutSummary = RPCMutationResult<"api.public.tess.auth.signOut">
type StoredAuthState = Partial<AuthSummary> & {
  encryptedSessionKey?: string
}

const StoreStatus = types.enumeration("TessSessionStoreStatus", ["idle", "loading", "loaded", "refreshing", "error"])

const ANONYMOUS_AUTH_SUMMARY: AuthSummary = {
  account: null,
  session: null,
}

const TessSessionStore = types
  .model("TessSessionStore", {
    account: types.frozen<AuthSummary["account"] | null>(null),
    encryptedSessionKey: types.maybe(types.string),
    lastError: types.maybeNull(types.string),
    session: types.frozen<AuthSummary["session"] | null>(null),
    status: types.optional(StoreStatus, "idle"),
  })
  .views((self) => ({
    get isAuthenticated() {
      return !!self.account || !!self.session?.isLoggedIn
    },
    get isSessionLoading() {
      return self.status === "loading" || self.status === "refreshing"
    },
  }))
  .actions((self) => ({
    applySummary(summary: AuthSummary | SignInSummary | SignOutSummary) {
      self.account = summary.account
      self.session = summary.session
      self.lastError = null
      self.status = "loaded"
    },
    clearSession() {
      self.account = null
      self.encryptedSessionKey = undefined
      self.lastError = null
      self.session = null
      self.status = "idle"
    },
    setEncryptedSessionKey(encryptedSessionKey: string) {
      self.encryptedSessionKey = encryptedSessionKey
    },
    setError(error: string) {
      self.lastError = error
      self.status = "error"
    },
    setPending() {
      self.lastError = null
      self.status = self.account || self.session ? "refreshing" : "loading"
    },
  }))
  .actions((self) => ({
    async refreshBrowserSessionKey() {
      const encryptedSessionKey = await getEncryptedSessionKey()
      self.setEncryptedSessionKey(encryptedSessionKey)
      return encryptedSessionKey
    },
  }))
  .actions((self) => ({
    async ensureEncryptedSessionKey() {
      if (self.encryptedSessionKey) {
        return self.encryptedSessionKey
      }

      return self.refreshBrowserSessionKey()
    },
    load() {
      const stored = safeStorage.getData<StoredAuthState>(STORAGE_KEY)

      if (!stored) {
        return
      }

      self.encryptedSessionKey = stored.encryptedSessionKey
      self.account = stored.account ?? null
      self.session = stored.session ?? null
      self.status = stored.account || stored.session?.isLoggedIn ? "loaded" : "idle"
    },
    save() {
      if (!self.encryptedSessionKey && !self.account && !self.session) {
        safeStorage.removeItem(STORAGE_KEY)
        return
      }

      safeStorage.setData<StoredAuthState>(STORAGE_KEY, {
        account: self.account,
        encryptedSessionKey: self.encryptedSessionKey,
        session: self.session,
      })
    },
  }))
  .actions((self) => ({
    async refresh(): Promise<Result<AuthSummary>> {
      self.setPending()

      try {
        const encryptedSessionKey = await self.refreshBrowserSessionKey()
        const summary = await rpcClient.query("api.public.tess.auth.session", { encryptedSessionKey })
        self.applySummary(summary)
        return { success: true, value: summary }
      } catch (error) {
        const message = getErrorMessage(error)
        self.setError(message)
        return { success: false, error: message }
      }
    },
    async signIn(email: string, password: string): Promise<Result<SignInSummary>> {
      self.setPending()

      try {
        const encryptedSessionKey = await self.refreshBrowserSessionKey()
        const summary = await rpcClient.mutation("api.public.tess.auth.signIn", {
          email,
          encryptedSessionKey,
          password,
        })
        self.applySummary(summary)
        return { success: true, value: summary }
      } catch (error) {
        const message = getErrorMessage(error)
        self.setError(message)
        return { success: false, error: message }
      }
    },
    async signOut(): Promise<Result<SignOutSummary | AuthSummary>> {
      if (!self.encryptedSessionKey) {
        self.clearSession()
        return { success: true, value: ANONYMOUS_AUTH_SUMMARY }
      }

      self.setPending()

      try {
        const summary = await rpcClient.mutation("api.public.tess.auth.signOut", {
          encryptedSessionKey: self.encryptedSessionKey,
        })
        self.applySummary(summary)
        return { success: true, value: summary }
      } catch (error) {
        const message = getErrorMessage(error)
        self.setError(message)
        return { success: false, error: message }
      }
    },
  }))
  .actions((self) => ({
    async bootstrap(): Promise<Result<AuthSummary>> {
      if (!env.client) {
        return { success: false, error: "Auth bootstrap only runs in the browser" }
      }

      return self.refresh()
    },
  }))

type Result<TValue = undefined, TError = string> =
  | {
      success: true
      value: TValue
    }
  | {
      success: false
      error: TError
    }

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error)
}

export const authStore = TessSessionStore.create({
  account: null,
  session: null,
})

if (env.client) {
  authStore.load()
  onSnapshot(authStore, () => {
    authStore.save()
  })
  authStore.bootstrap()

  // @ts-ignore
  window.authStore = authStore
}
